import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaBox, 
  FaTag, 
  FaShoppingCart, 
  FaUsers,
  FaExclamationTriangle,
  FaSync
} from 'react-icons/fa';
import { productsApi, categoriesApi, stockApi, authApi } from '../services/api';
import './Dashboard.css';

const Dashboard = () => {
  const [summaryData, setSummaryData] = useState({
    products: { count: 0, totalValue: 0, loading: true, error: null },
    categories: { count: 0, loading: true, error: null },
    orders: { count: 0, totalRevenue: 0, loading: true, error: null },
    users: { count: 0, loading: true, error: null }
  });
  const [lowStockItems, setLowStockItems] = useState([]);
  const [lowStockLoading, setLowStockLoading] = useState(true);
  const [lowStockError, setLowStockError] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchDashboardData = useCallback(async (isRefresh = false) => {
    if (isRefresh) {
      setIsRefreshing(true);
    }

    // Fetch products
    try {
      const productsResponse = await productsApi.getAll();
      const products = Array.isArray(productsResponse.data?.data?.products) 
        ? productsResponse.data.data.products 
        : [];
      
      // Calculate total inventory value
      const totalValue = products.reduce((sum, product) => 
        sum + (product.stock * product.price), 0
      );

      setSummaryData(prev => ({
        ...prev,
        products: { 
          count: products.length, 
          totalValue,
          loading: false, 
          error: null 
        }
      }));

      // Update low stock items
      const lowStock = products
        .filter(p => p.stock === 0)
        .slice(0, 3);
      setLowStockItems(lowStock);
      setLowStockLoading(false);
      setLowStockError(null);
    } catch (error) {
      setSummaryData(prev => ({
        ...prev,
        products: { 
          count: prev.products.count, 
          totalValue: prev.products.totalValue,
          loading: false, 
          error: 'Failed to load products'
        }
      }));
      setLowStockError('Failed to load low stock items');
    }

    // Fetch categories
    try {
      const categoriesResponse = await categoriesApi.getAll();
      const categories = Array.isArray(categoriesResponse.data?.data?.categories) 
        ? categoriesResponse.data.data.categories 
        : [];
      setSummaryData(prev => ({
        ...prev,
        categories: { count: categories.length, loading: false, error: null }
      }));
    } catch (error) {
      setSummaryData(prev => ({
        ...prev,
        categories: { 
          count: prev.categories.count, 
          loading: false, 
          error: 'Failed to load categories'
        }
      }));
    }

    // Fetch transactions
    try {
      const transactionsResponse = await stockApi.getAll();
      const transactions = Array.isArray(transactionsResponse.data?.data?.stockMovements) 
        ? transactionsResponse.data.data.stockMovements 
        : [];
      
      // Calculate total revenue from outgoing transactions
      const totalRevenue = transactions
        .filter(t => t.type === 'out')
        .reduce((sum, t) => sum + (t.quantity * t.unitPrice), 0);

      setSummaryData(prev => ({
        ...prev,
        orders: { 
          count: transactions.filter(t => t.type === 'out').length, 
          totalRevenue,
          loading: false, 
          error: null 
        }
      }));
    } catch (error) {
      setSummaryData(prev => ({
        ...prev,
        orders: { 
          count: prev.orders.count, 
          totalRevenue: prev.orders.totalRevenue,
          loading: false, 
          error: 'Failed to load orders'
        }
      }));
    }

    // Fetch users
    try {
      const usersResponse = await authApi.getUsers();
      const users = Array.isArray(usersResponse.data?.data?.users) 
        ? usersResponse.data.data.users 
        : [];
      setSummaryData(prev => ({
        ...prev,
        users: { count: users.length, loading: false, error: null }
      }));
    } catch (error) {
      setSummaryData(prev => ({
        ...prev,
        users: { 
          count: prev.users.count, 
          loading: false, 
          error: 'Failed to load users'
        }
      }));
    }

    if (isRefreshing) {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const handleRefresh = () => {
    fetchDashboardData(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const summaryCards = [
    { 
      title: 'Total Products', 
      data: summaryData.products,
      icon: <FaBox />, 
      color: 'primary',
      path: '/products',
      renderValue: (data) => (
        <>
          <h3>{data.count.toLocaleString()}</h3>
          <div className="card-subtitle">Value: {formatCurrency(data.totalValue)}</div>
        </>
      )
    },
    { 
      title: 'Categories', 
      data: summaryData.categories,
      icon: <FaTag />, 
      color: 'info',
      path: '/categories',
      renderValue: (data) => <h3>{data.count.toLocaleString()}</h3>
    },
    { 
      title: 'Orders', 
      data: summaryData.orders,
      icon: <FaShoppingCart />, 
      color: 'success',
      path: '/transactions/sales',
      renderValue: (data) => (
        <>
          <h3>{data.count.toLocaleString()}</h3>
          <div className="card-subtitle">Revenue: {formatCurrency(data.totalRevenue)}</div>
        </>
      )
    },
    { 
      title: 'Users', 
      data: summaryData.users,
      icon: <FaUsers />, 
      color: 'warning',
      path: '/users',
      renderValue: (data) => <h3>{data.count.toLocaleString()}</h3>
    }
  ];

  const renderCardContent = (card) => {
    if (card.data.loading) {
      return <div className="skeleton-loader"></div>;
    }
    if (card.data.error) {
      return <div className="error-text">Error loading data</div>;
    }
    return card.renderValue(card.data);
  };

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Inventory Dashboard</h1>
          <p className="text-muted">Welcome back! Here's your inventory overview.</p>
        </div>
        <button 
          className={`refresh-button ${isRefreshing ? 'spinning' : ''}`}
          onClick={handleRefresh}
          disabled={isRefreshing}
        >
          <FaSync /> Refresh
        </button>
      </div>

      {/* Summary Cards */}
      <div className="summary-cards">
        {summaryCards.map((card, index) => (
          <div key={index} className={`summary-card ${card.color}`}>
            <div className="card-icon">
              {card.icon}
            </div>
            <div className="card-content">
              {renderCardContent(card)}
              <p>{card.title}</p>
            </div>
            <Link to={card.path} className="card-link">View</Link>
          </div>
        ))}
      </div>

      {/* Low Stock Alerts */}
      <div className="dashboard-card">
        <div className="card-header">
          <h2>Low Stock Alerts</h2>
          <Link to="/inventory/stock" className="btn-link">Manage Stock</Link>
        </div>
        <div className="card-body">
          {lowStockLoading ? (
            <div className="skeleton-list">
              {[1, 2, 3].map(i => (
                <div key={i} className="skeleton-item">
                  <div className="skeleton-loader"></div>
                </div>
              ))}
            </div>
          ) : lowStockError ? (
            <div className="error-message">
              <FaExclamationTriangle /> {lowStockError}
            </div>
          ) : lowStockItems.length === 0 ? (
            <div className="empty-state">
              <p>No low stock items to display</p>
            </div>
          ) : (
            <ul className="low-stock-list">
              {lowStockItems.map(item => (
                <li key={item.id} className="low-stock-item">
                  <div className="item-info">
                    <span className="item-name">{item.name}</span>
                    <span className="current-stock">Current: {item.stock}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
